import { useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import Page from '../../components/layouts/page'
import AppDialog from '../../components/app_dialog'
import { Badge, EmptyState, Tooltip } from '@cinesend/ui-kit'
import DataTable from '../../components/data_table'
import RowMenu from '../../components/row_menu'
import useTableFilters from '../../hooks/use_table_filters'
import { humanFileSize } from '../../helpers/human_file_size'
import { useState } from 'react'
import ReactJson from 'react-json-view'
import dayjs from 'dayjs'
import { ArrowsClockwise, Check, WarningDiamond } from '@phosphor-icons/react'
import { convertToShorthandWithTimezone } from '../../helpers/convert_date'
import useAuth from '../../hooks/use_auth'

const FRESH_SYNC_MINUTES = 10

const formatFileDuration = (ms) => {
  if (ms == null || ms === '') return 'N/A'
  const totalMs = Number(ms)
  if (!Number.isFinite(totalMs) || totalMs < 0) return 'N/A'
  const totalSeconds = Math.floor(totalMs / 1000)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  return [hours, minutes, seconds].map((part) => String(part).padStart(2, '0')).join(':')
}

const SyncStatusEntry = ({ label, data }) => {
  const lastRun = data?.last_run
  const lastSuccess = data?.last_successful_run

  if (!lastRun) {
    return (
      <span>
        <strong>{label}:</strong> No sync has run yet.
      </span>
    )
  }

  if (lastRun.status === 'pending') {
    return (
      <span>
        <strong>{label}:</strong> Sync in progress&hellip;
      </span>
    )
  }

  if (lastRun.status === 'success') {
    return (
      <span>
        <strong>{label}:</strong> Last synced {convertToShorthandWithTimezone(lastRun.created_at)} &mdash;{' '}
        {lastRun.files_synced} file(s)
      </span>
    )
  }

  if (lastRun.status === 'error') {
    return (
      <span>
        <strong>{label}:</strong> Last sync failed ({convertToShorthandWithTimezone(lastRun.created_at)}).{' '}
        {lastSuccess ? (
          <>
            Showing data from last successful sync: {convertToShorthandWithTimezone(lastSuccess.created_at)} &mdash;{' '}
            {lastSuccess.files_synced} file(s).
          </>
        ) : (
          'No successful sync on record.'
        )}
      </span>
    )
  }

  return null
}

const runState = (data) => {
  const lastRun = data?.last_run
  if (lastRun?.status === 'pending') return 'syncing'
  if (lastRun?.status === 'error') return 'warning'
  if (
    lastRun?.status === 'success' &&
    dayjs(lastRun.created_at).isAfter(dayjs().subtract(FRESH_SYNC_MINUTES, 'minute'))
  ) {
    return 'fresh'
  }
  return 'warning'
}

const SyncStatusIcon = ({ mirrorRuns }) => {
  if (!mirrorRuns) return null

  const states = [runState(mirrorRuns.audio), runState(mirrorRuns.video)]
  const status = states.includes('syncing')
    ? 'syncing'
    : states.every((state) => state === 'fresh')
      ? 'fresh'
      : 'warning'
  const icon =
    status === 'syncing' ? (
      <ArrowsClockwise size={18} weight='bold' className='animate-spin cs-sync-spin' aria-hidden='true' />
    ) : status === 'fresh' ? (
      <Check size={18} weight='bold' aria-hidden='true' />
    ) : (
      <WarningDiamond size={18} weight='bold' aria-hidden='true' />
    )
  const label =
    status === 'syncing' ? 'Sync in progress' : status === 'fresh' ? 'Synced recently' : 'Sync needs attention'

  return (
    <Tooltip
      side='bottom'
      align='start'
      title={
        <span className='flex flex-col gap-1 text-left'>
          <SyncStatusEntry label='Audio' data={mirrorRuns.audio} />
          <SyncStatusEntry label='Video' data={mirrorRuns.video} />
        </span>
      }
      trigger={
        <button type='button' aria-label={label} className='inline-flex border-0 bg-transparent p-0 text-gray-400'>
          {icon}
        </button>
      }
    />
  )
}

const Files = () => {
  const [modalOpen, setModalOpen] = useState(null)
  const { airlineID } = useParams()
  const { user } = useAuth()

  const sortingOptions = [
    { label: 'File Name', key: 'uploaded_file_name' },
    { label: 'File Size', key: 'file_size' },
    { label: 'Received', key: 'received_time' },
    { label: 'Status', key: 'status' },
    { label: 'Matched Status', key: 'is_matched' },
  ]

  // const filteringOptions = [
  //   {
  //     label: 'Matched Status',
  //     key: 'is_matched',
  //     type: 'checkboxes',
  //     options: [
  //       { label: 'Matched', key: 'true' },
  //       { label: 'Unmatched', key: 'false' },
  //     ],
  //   },
  // ]

  const { queryString, searching, sorting, filtering, pagination } = useTableFilters({
    sortingOptions,
    // filteringOptions,
    enableSearch: true,
  })

  const {
    data: externalFiles,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [`airlines/${airlineID}/external-files-mirror`, queryString],
    enabled: !!airlineID,
  })

  const columns = [
    {
      key: 'uploaded_file_name',
      header: 'File Name',
      render: (data) => data.file_name,
    },
    {
      key: 'duration',
      header: 'Duration',
      render: (data) =>
        formatFileDuration(data.raw_response?.duration ?? data.raw_response?.file?.durationMs ?? data.duration),
    },
    {
      key: 'file_size',
      header: 'File Size',
      render: (data) => humanFileSize(data.file_size),
    },
    {
      key: 'supplier',
      header: 'Supplier',
      render: (data) => data.supplier ?? 'N/A',
    },
    {
      key: 'audio_languages',
      header: 'Audio Languages',
      render: (data) => {
        const unknownLanguages = data.unknown_languages || []
        return data.audio_languages?.length > 0 ? (
          <div className='flex flex-wrap gap-1'>
            {data.audio_languages.map((lang, index) => (
              <Badge key={index} tone={unknownLanguages.includes(lang) ? 'error' : 'neutral'}>
                {lang}
              </Badge>
            ))}
          </div>
        ) : (
          'None'
        )
      },
    },
    {
      key: 'text_track_languages',
      header: 'Text Track Languages',
      render: (data) => {
        const unknownLanguages = data.unknown_languages || []
        return data.text_track_languages?.length > 0 ? (
          <div className='flex flex-wrap gap-1'>
            {data.text_track_languages.map((lang, index) => (
              <Badge key={index} tone={unknownLanguages.includes(lang) ? 'error' : 'neutral'}>
                {lang}
              </Badge>
            ))}
          </div>
        ) : (
          'None'
        )
      },
    },
    {
      key: 'received_time',
      header: 'Received',
      render: (data) => (data.received_time ? dayjs(data.received_time).format('MMM D, YYYY, h:mm A') : 'N/A'),
    },
    {
      key: 'status',
      header: 'Status',
      render: (data) => (
        <Badge
          tone={
            data.status === 'completed' ? 'success' : data.status === 'completed-with-warnings' ? 'warning' : 'error'
          }
        >
          {data.status?.toUpperCase()}
        </Badge>
      ),
    },
    {
      key: 'is_matched',
      header: 'Matched',
      render: (data) => (
        <Badge tone={data.is_matched ? 'success' : 'warning'}>{data.is_matched ? 'Matched' : 'Unmatched'}</Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '65px',
      render: (data) => (
        <RowMenu
          label={`Actions for ${data.file_name}`}
          items={[
            user?.has_unrestricted_edit_role && {
              id: 'raw-response',
              label: 'View raw response',
              onSelect: () => setModalOpen(data?.raw_response ?? {}),
            },
          ].filter(Boolean)}
        />
      ),
    },
  ]

  return (
    <Page
      title={
        <span className='inline-flex items-center gap-3'>
          External Files
          <SyncStatusIcon mirrorRuns={externalFiles?.mirror_runs} />
        </span>
      }
      breadcrumbs={[{ text: 'External Files' }]}
    >
      <DataTable
        caption='External files'
        columns={columns}
        rows={externalFiles?.files?.data || []}
        isLoading={isLoading}
        error={error}
        onRetry={refetch}
        empty={<EmptyState title='No external files found!' detail='No files have been synced yet.' />}
        sorting={sorting}
        searching={searching}
        pagination={pagination}
        totalRows={externalFiles?.files?.total || 0}
      />
      {modalOpen && (
        <AppDialog title='Raw response' onClose={() => setModalOpen(null)}>
          <ReactJson src={modalOpen} />
        </AppDialog>
      )}
    </Page>
  )
}

export default Files
